import * as React from "react"
import { usePostHog } from "@posthog/react"

import { isPostHogActive } from "@/instrument"
import { FEATURE_FLAG_FALLBACK, type FeatureFlagKey } from "@ibada/shared-types/feature-flags"

/**
 * Local override for development. Consulted ONLY when PostHog has no answer — which, because PostHog
 * initialises in real production only, means `vite dev` and preview builds: exactly where it is needed and
 * nowhere it could contradict the dashboard. Sources, in order: `localStorage["ibada:flags"]` (e.g.
 * `{"reports-beta": true}`) then the build-time `VITE_FLAG_OVERRIDES` JSON. Undefined when not overridden.
 */
function readFlagOverride(flag: FeatureFlagKey): boolean | undefined {
  if (typeof window !== "undefined") {
    try {
      const raw = window.localStorage.getItem("ibada:flags")
      if (raw) {
        const map = JSON.parse(raw) as Record<string, unknown>
        if (Object.prototype.hasOwnProperty.call(map, flag)) return Boolean(map[flag])
      }
    } catch {
      // ignore malformed override JSON
    }
  }

  const envRaw = import.meta.env.VITE_FLAG_OVERRIDES as string | undefined
  if (envRaw) {
    try {
      const map = JSON.parse(envRaw) as Record<string, unknown>
      if (Object.prototype.hasOwnProperty.call(map, flag)) return Boolean(map[flag])
    } catch {
      // ignore malformed override JSON
    }
  }

  return undefined
}

// MODULE-LEVEL "have PostHog flags loaded at least once" signal, shared by every hook instance. This must
// not be per-instance: a freshly-mounted instance (e.g. a route guard) would otherwise start "not loaded",
// fall back to the default, and wrongly redirect even though the client already has the flags. Once loaded,
// any instance reads isFeatureEnabled synchronously and correctly on its first render.
let flagsLoaded = false
let flagSubscriptionStarted = false
const flagSubscribers = new Set<() => void>()

function ensureFlagSubscription(client: ReturnType<typeof usePostHog>): void {
  if (flagSubscriptionStarted || !isPostHogActive || !client) return
  flagSubscriptionStarted = true
  // Fires when flags first load (immediately if already loaded) and on every reload (route change / focus).
  client.onFeatureFlags(() => {
    flagsLoaded = true
    flagSubscribers.forEach((notify) => notify())
  })
}

function useFlagSubscription(): ReturnType<typeof usePostHog> {
  const client = usePostHog()
  const [, forceRender] = React.useReducer((tick: number) => tick + 1, 0)
  React.useEffect(() => {
    ensureFlagSubscription(client)
    flagSubscribers.add(forceRender)
    return () => {
      flagSubscribers.delete(forceRender)
    }
  }, [client])
  return client
}

/**
 * Read a single feature flag. Resolution order: POSTHOG -> local override -> fail-safe default.
 *
 * POSTHOG WINS. Once its flags have loaded, its answer is the answer: a disabled / 0% flag
 * (`isFeatureEnabled` -> undefined) reads OFF (so it can be turned off), and an active flag reads true
 * synchronously (so a route guard never wrongly redirects before its own callback fires).
 *
 * The override used to be read FIRST, which meant a `VITE_FLAG_OVERRIDES` value baked into a build, or a
 * stale `localStorage["ibada:flags"]` left in someone's browser months ago, silently outranked the dashboard
 * everyone believes is in charge — and nothing on screen said so. It now applies only where PostHog has no
 * answer, which (because PostHog initialises in real production only) is still every dev and preview build.
 *
 * This is UX only. Anything security/billing-sensitive must ALSO be enforced on the server.
 */
export function useFeatureFlag(flag: FeatureFlagKey | null): boolean {
  const client = useFlagSubscription()

  // Some capabilities are released and intentionally have no PostHog gate. Keep the hook unconditional so
  // callers can resolve any capability without violating React's rules of hooks.
  if (flag === null) return true

  if (isPostHogActive && client && flagsLoaded) return client.isFeatureEnabled(flag) === true

  const override = readFlagOverride(flag)
  if (override !== undefined) return override

  // Dev used to return `true` for EVERY flag, so a paused feature was still fully visible locally and the
  // only place the pause existed was production. That makes the flag a lie in the one environment where it
  // is exercised daily: you cannot see what a customer sees. Dev honours the same fail-safe as prod.
  //
  // To work on a paused feature, un-pause it FOR YOURSELF (PostHog is inactive in dev, so this decides):
  //   localStorage.setItem("ibada:flags", JSON.stringify({ "budgeting": true }))
  // or set VITE_FLAG_OVERRIDES at build time.
  return FEATURE_FLAG_FALLBACK[flag]
}

/**
 * True once PostHog flags have loaded (or PostHog is inactive, e.g. local dev). A route guard should wait on
 * this before redirecting, so it doesn't redirect on the fail-safe default during the brief initial load
 * (e.g. a hard refresh / deep link onto a flagged page).
 */
export function useFlagsReady(): boolean {
  useFlagSubscription()
  return !isPostHogActive || flagsLoaded
}
